import { Tab, tabIDToType } from "@store/minersSelect/actionTypes";
import { settingsCacheKey } from "@utils/hooks/useCurrentUser";
import { clearCache } from "@utils/localCache";
import { MTFiltersValuesI, MoveTargetTabs } from "./getMinerList";
import { PostJsonRequest, PostRequest } from "./http/BaseRequest";
import { ITag } from "./http/responses/AccountResponse";
import { StringDecoder } from "string_decoder";

export interface MinerParamsSettingsI extends MTFiltersValuesI {
  miner: string; // - miner1,miner2....
  speed_drop?: number;
  notifications?: string;
  pool_id?: string;
  miner_smooth?: boolean;
}
export interface UserParamSettingsI extends MinerParamsSettingsI {
  user_note?: string;
}

export interface AdminParamSettingsI extends MinerParamsSettingsI {
  customer?: string;
  miner_type?: string;
  default_speed?: string;
  mining_persent?: number;
  admin_note?: string;
  miner_id?: string;
  id?: number;
  // название пула админа
  admin_setting?: string;
  location_id?: string;
  sn?: string;
  pn?: string;
  ip?: string;
  mac?: string;
  port?: string;
  set_install_time?: string;
}

export interface AdminChangeRecord {
  miner_id: string;
  note_id: string;
}
/*
export interface AdminChangeResponseIRecord extends ServerResponse {
  Record?: AdminChangeRecord[];
}
*/

const modify: {
  [key: string]: {
    (src: { [key: string]: any }, target: { [key: string]: any }): void;
  };
} = {
  miner: (src, target) => {
    target.id = (src.miner as string).split(",").map((item) => Number(item));
  },
  contractid: (src, target) => {
    target.contractId = Number(src.contractid);
  },
  miner_plan: (src, target) => {
    target.planId = Number(src.miner_plan);
  },
  profile_id: (src, target) => {
    target.profileId = Number(src.profile_id);
  },
  location_id: (src, target) => {
    target.locationId = Number(src.location_id);
  },
  set_install_time: (src, target) => {
    target.installTime = src.set_install_time;
  },
  pool_id: (src, target) => {
    target.settingId = Number(src.pool_id);
  },
  customer: (src, target) => {
    target.userId = Number(src.customer);
  },
  mining_persent: (src, target) => {
    target.proof = Number(src.mining_persent);
  },
  speed_drop: (src, target) => {
    target.dropSpeed = Number(src.speed_drop);
  },
  miner_id: (src, target) => {
    target.name = src.miner_id;
  },
  notifications: (src, target) => {
    target.notifications = (src.notifications as string).split(",");
  },
  admin_setting: (src, target) => {
    target.proofSettingId = Number(src.admin_setting);
  },
  admin_note: (src, target) => {
    target.adminNote = src.admin_note;
  },
  user_note: (src, target) => {
    target.userNote = src.user_note;
  },
};

function modifySettings(params: any): any {
  const result: { [key: string]: any } = {};
  for (const key in params) {
    if (key in modify) {
      modify[key as keyof typeof modify](params, result);
    } else {
      result[key] = params[key];
    }
  }
  return result;
}

export class AdminChangeSettingsRequest extends PostJsonRequest {
  constructor(params: AdminParamSettingsI) {
    if (params.search) {
      (params as any).search = JSON.stringify(params.search);
    }
    clearCache(settingsCacheKey);
    super("/miner/settings/change", modifySettings(params));
  }
}

export class UserChangeSettingsRequest extends PostJsonRequest {
  constructor(params: UserParamSettingsI) {
    if (params.search) {
      (params as any).search = JSON.stringify(params.search);
    }
    clearCache(settingsCacheKey);
    super("/miner/settings/change", modifySettings(params));
  }
}

export class AcceptMinersTagRequest extends PostRequest {
  constructor(tags: ITag, miners: string) {
    clearCache(settingsCacheKey);
    super("/miner/settings/change", {
      tags,
      id: [miners.split(",").map((m) => Number(m))],
    });
  }
}

export interface IMinerMeta {
  ip?: string;
  mac?: string;
  port?: string;
  pn?: string;
  sn?: string;
  password?: string;
  ucp?: boolean;
  set_install_time?: string;
  flagText?: string;
  flagColor?: string;
  user?: string;
}

export function formatMinerMeta(meta: IMinerMeta, separator: string = '\n'): string {
  // ip, mac, серийный номер, порт, пароль
  const headerMap: Partial<Record<keyof IMinerMeta, string>> = {
    ip: 'IP',
    mac: 'MAC',
    sn: 'SN',
    port: 'Port',
    password: 'Password',
    // pn: 'PN',
    // ucp: 'UCP',
    // set_install_time: 'Install Time',
    // flagText: 'Flag',
    // flagColor: 'Flag Color',
    // user: 'User'
  };

  const isValid = (value: any): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };

  const parts = Object.entries(meta)
    .filter(([_, value]) => isValid(value))
    .map(([key, value]) => {
      const header = headerMap[key as keyof IMinerMeta];// || key;
      if (!header) return "";
      const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
      return `${header}: ${display}`;
    });

  return parts.filter(x => !!x).join(separator);
}

export class AdminChangeDataSettingsRequest extends PostJsonRequest {
  constructor(id: number, params: IMinerMeta) {
    super("/miner/settings/change", { id: [id], meta: params });
  }
}

// export class AdminSetMinerBroken extends PostRequest {
//   constructor(minerID: string, broken: boolean) {
//     super("/admin/miner/set_broken", { id: minerID, is_broken: broken });
//   }
// }

export const setMinerFlagRequest = {
  makeRequest(id: number, meta: unknown) {
    return new PostJsonRequest(`/miner/settings/change`, { id: [id], meta });
  },
};

export const moveMinerToSpecialTabRequest = {
  makeRequest(tabID: Tab, minerIDs: string[], move = false) {
    const tab = tabIDToType(tabID);
    if (typeof tab === "number") return;
    if (!tab || !MoveTargetTabs.has(tab)) return;
    return new PostRequest(`/admin/miner/set_${tab}`, {
      id: minerIDs.join(","),
      [`is_${tab}`]: move,
    });
  },
};